import { App } from 'antd';
import {
  isForceRetryError,
  isHTTPError,
  isKyError,
  isNetworkError,
  isTimeoutError,
} from 'ky';
import { useCallback, useEffect } from 'react';
import { redirectToLoginPage } from '../auth/api';
import { clearAccessToken } from '../auth/utils';

/**
 * 自动处理未处理的异常
 */
export const useGobalErrorHandler = () => {
  const { notification } = App.useApp();

  const handleError = useCallback(
    (error: unknown) => {
      if (isKyError(error)) {
        if (isHTTPError(error)) {
          const { response } = error;

          if (response.status >= 500) {
            notification.error({
              title: '错误',
              description:
                error.message || '服务端发生未知的错误，请联系管理员',
            });
            return;
          }

          if (response.status === 401) {
            clearAccessToken();
            redirectToLoginPage();
            return;
          }

          if (response.status >= 400 && response.status < 500) {
            notification.error({
              title: '错误',
              description: error.message || '客户端发生未知的错误',
            });
            return;
          }
        }

        if (isTimeoutError(error)) {
          notification.error({
            title: '错误',
            description: error.message || '请求超时，请重试',
          });
          return;
        }

        if (isForceRetryError(error)) {
          notification.error({
            title: '错误',
            description: error.message || '请求需要重试',
          });
          return;
        }

        if (isNetworkError(error)) {
          notification.error({
            title: '网络异常',
            description: '请检查网络是否正常',
          });
        }

        return;
      }

      if (error instanceof Error) {
        console.error(error);
        notification.error({
          title: '错误',
          description: error.message,
        });
        return;
      }

      // 除了 BizError 这种自定义错误会使用其自带的 message 展示错误内容外，其他无法识别的错
      // 误均视为未知错误这里仅弹出一个固定的错误提示来告知用户当前的操作出现了异常
      console.error(error);
      notification.error({
        title: '错误',
        description: '发生未知的错误',
      });
    },
    [notification],
  );

  useEffect(() => {
    const onUnhandledRejection = (e: PromiseRejectionEvent) => {
      handleError(e.reason);
    };

    window.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, [handleError]);
};
